import { Sport } from '../types/core';

export interface CalculatedStats {
  [key: string]: number;
}

/**
 * Calculate baseball/softball statistics from raw stat entries.
 * @param innings - number of innings per game for ERA normalisation (defaults to 7)
 */
export function calculateBaseballStats(rawStats: Array<{ stat_key: string; stat_value: number }>, innings = 7): CalculatedStats {
  const stats: Record<string, number> = {};
  
  // Sum up all stat values by key
  rawStats.forEach(stat => {
    if (!stats[stat.stat_key]) {
      stats[stat.stat_key] = 0;
    }
    stats[stat.stat_key] += parseFloat(stat.stat_value.toString());
  });

  const calculated: CalculatedStats = {};

  // Batting Average (AVG) = H / AB
  if (stats['H'] !== undefined && stats['AB'] !== undefined && stats['AB'] > 0) {
    calculated['AVG'] = stats['H'] / stats['AB'];
  }

  // On-Base Percentage (OBP) = (H + BB + HBP) / (AB + BB + HBP + SF)
  const hits = stats['H'] || 0;
  const walks = stats['BB'] || 0;
  const hitByPitch = stats['HBP'] || 0;
  const sacrificeFly = stats['SF'] || 0;
  const atBats = stats['AB'] || 0;
  
  const numerator = hits + walks + hitByPitch;
  const denominator = atBats + walks + hitByPitch + sacrificeFly;
  
  if (denominator > 0) {
    calculated['OBP'] = numerator / denominator;
  }

  // Slugging Percentage (SLG) = Total Bases / AB
  // Total Bases = 1B + (2B * 2) + (3B * 3) + (HR * 4)
  // If we have individual hit types, use them; otherwise use H as approximation
  const singles = stats['1B'] || 0;
  const doubles = stats['2B'] || 0;
  const triples = stats['3B'] || 0;
  const homeRuns = stats['HR'] || 0;
  
  // If we have individual hit types, calculate total bases accurately
  // Otherwise, estimate: H - (2B + 3B + HR) = singles, then calculate
  let totalBases = 0;
  if (singles > 0 || doubles > 0 || triples > 0 || homeRuns > 0) {
    // We have individual hit types
    totalBases = singles + (doubles * 2) + (triples * 3) + (homeRuns * 4);
  } else if (hits > 0) {
    // Estimate: assume all hits are singles (conservative estimate)
    // This is not ideal, but better than nothing
    totalBases = hits;
  }
  
  if (atBats > 0 && totalBases > 0) {
    calculated['SLG'] = totalBases / atBats;
  }

  // On-Base Plus Slugging (OPS) = OBP + SLG
  if (calculated['OBP'] !== undefined && calculated['SLG'] !== undefined) {
    calculated['OPS'] = calculated['OBP'] + calculated['SLG'];
  }

  // ERA = (Earned Runs × innings) / Innings Pitched, normalised to the team's game length.
  const inningsPitchedForEra = stats['IP'] || 0;
  const earnedRuns = stats['ER'] ?? stats['EarnedRuns'];
  if (inningsPitchedForEra > 0 && earnedRuns !== undefined) {
    calculated['ERA'] = (earnedRuns * (innings || 7)) / inningsPitchedForEra;
  }

  // Walks + Hits per Inning Pitched (WHIP) = (BB_Allowed + H_Allowed) / IP
  const inningsPitched = stats['IP'] || 0;
  const hitsAllowed = stats['H_Allowed'] || 0;
  const walksAllowed = stats['BB_Allowed'] || 0;
  
  if (inningsPitched > 0 && (hitsAllowed > 0 || walksAllowed > 0)) {
    calculated['WHIP'] = (walksAllowed + hitsAllowed) / inningsPitched;
  }

  return calculated;
}

/**
 * Calculate basketball statistics from raw stat entries
 */
export function calculateBasketballStats(rawStats: Array<{ stat_key: string; stat_value: number }>): CalculatedStats {
  const stats: Record<string, number> = {};
  rawStats.forEach(s => {
    stats[s.stat_key] = (stats[s.stat_key] || 0) + parseFloat(s.stat_value.toString());
  });

  const calculated: CalculatedStats = {};

  // FG% = FGM / FGA
  if (stats['FGM'] !== undefined && stats['FGA'] !== undefined && stats['FGA'] > 0) {
    calculated['FG%'] = stats['FGM'] / stats['FGA'];
  }
  // FT% = FTM / FTA
  if (stats['FTM'] !== undefined && stats['FTA'] !== undefined && stats['FTA'] > 0) {
    calculated['FT%'] = stats['FTM'] / stats['FTA'];
  }

  return calculated;
}

/**
 * Calculate football/flag football statistics from raw stat entries.
 */
export function calculateFootballStats(rawStats: Array<{ stat_key: string; stat_value: number }>): CalculatedStats {
  const stats: Record<string, number> = {};
  rawStats.forEach(s => {
    stats[s.stat_key] = (stats[s.stat_key] || 0) + parseFloat(s.stat_value.toString());
  });

  const calculated: CalculatedStats = {};

  // Completion % = PassComp / PassAtt
  if (stats['PassComp'] !== undefined && stats['PassAtt'] > 0) {
    calculated['CompPct'] = stats['PassComp'] / stats['PassAtt'];
  }
  // Yards Per Carry = RushYds / RushAtt
  if (stats['RushYds'] !== undefined && stats['RushAtt'] > 0) {
    calculated['YPC'] = stats['RushYds'] / stats['RushAtt'];
  }
  // Yards Per Reception = RecYds / Rec
  if (stats['RecYds'] !== undefined && stats['Rec'] > 0) {
    calculated['YPR'] = stats['RecYds'] / stats['Rec'];
  }

  return calculated;
}

/**
 * Calculate Tennis statistics from raw stat entries (sc-339 / Phase 5).
 * Keys match src/services/tennis/statsProjection.ts's raw counting stats.
 */
export function calculateTennisStats(rawStats: Array<{ stat_key: string; stat_value: number }>): CalculatedStats {
  const stats: Record<string, number> = {};
  rawStats.forEach((s) => {
    stats[s.stat_key] = (stats[s.stat_key] || 0) + parseFloat(s.stat_value.toString());
  });

  const calculated: CalculatedStats = {};
  const ratio = (num: number | undefined, den: number | undefined): number | null =>
    num !== undefined && den !== undefined && den > 0 ? num / den : null;

  const firstServePct = ratio(stats['ServePts_1stIn'], stats['ServePts_1st']);
  if (firstServePct !== null) calculated['FirstServePct'] = firstServePct;

  const firstServeWinPct = ratio(stats['ServePts_1stWon'], stats['ServePts_1stIn']);
  if (firstServeWinPct !== null) calculated['FirstServeWinPct'] = firstServeWinPct;

  const secondServeWinPct = ratio(stats['ServePts_2ndWon'], stats['ServePts_2nd']);
  if (secondServeWinPct !== null) calculated['SecondServeWinPct'] = secondServeWinPct;

  const holdPct = ratio(stats['SvcGamesHeld'], stats['SvcGames']);
  if (holdPct !== null) calculated['HoldPct'] = holdPct;

  // % of break points played (while returning) that were converted.
  const breakPct = ratio(stats['BreakPtsWon'], stats['BreakPtsPlayed']);
  if (breakPct !== null) calculated['BreakPct'] = breakPct;

  if (stats['UnforcedErrors'] !== undefined && stats['UnforcedErrors'] > 0) {
    calculated['WUERatio'] = (stats['Winners'] || 0) / stats['UnforcedErrors'];
  }

  // "First strike" = win rate in short (1-3 shot) rallies, i.e. serve/return dominance before a real rally starts.
  const firstStrikePct = ratio(stats['Rally_1to3Won'], stats['Rally_1to3']);
  if (firstStrikePct !== null) calculated['FirstStrikePct'] = firstStrikePct;

  const netWinPct = ratio(stats['NetPtsWon'], stats['NetPts']);
  if (netWinPct !== null) calculated['NetWinPct'] = netWinPct;

  return calculated;
}

/**
 * Get calculated stats for a sport based on raw stats
 */
export function getCalculatedStats(
  sport: Sport,
  rawStats: Array<{ stat_key: string; stat_value: number }>,
  innings?: number
): CalculatedStats {
  if (sport === 'Baseball' || sport === 'Softball') {
    return calculateBaseballStats(rawStats, innings);
  }
  if (sport === 'Basketball') {
    return calculateBasketballStats(rawStats);
  }
  if (sport === 'Football' || sport === 'FlagFootball') {
    return calculateFootballStats(rawStats);
  }
  if (sport === 'Tennis') {
    return calculateTennisStats(rawStats);
  }
  return {};
}

/**
 * Get display label for a calculated stat
 * Returns the abbreviation (key) itself since we're using abbreviations
 */
export function getCalculatedStatLabel(statKey: string): string {
  // Just return the key as the label (we're using abbreviations)
  return statKey;
}

/**
 * Format a calculated stat value for display
 */
export function formatCalculatedStat(value: number, statKey: string): string {
  // Stats that should be displayed as percentages (0.000 format)
  const percentageStats = ['AVG', 'OBP', 'SLG', 'OPS', 'FG%', 'FT%'];

  if (percentageStats.includes(statKey)) {
    return value.toFixed(3);
  }

  // Stats that should be displayed as a whole-number percentage (e.g. "64%")
  const wholePercentStats = ['CompPct', 'FirstServePct', 'FirstServeWinPct', 'SecondServeWinPct', 'HoldPct', 'BreakPct', 'FirstStrikePct', 'NetWinPct'];
  if (wholePercentStats.includes(statKey)) {
    return `${Math.round(value * 100)}%`;
  }

  // Stats that should be displayed as a ratio (e.g. "1.4")
  if (statKey === 'WUERatio') {
    return value.toFixed(1);
  }

  // Stats that should be displayed with 2 decimal places
  const decimalStats = ['ERA', 'WHIP'];
  if (decimalStats.includes(statKey)) {
    return value.toFixed(2);
  }

  // Stats that should be displayed with 1 decimal place
  const oneDecimalStats = ['YPC', 'YPR'];
  if (oneDecimalStats.includes(statKey)) {
    return value.toFixed(1);
  }

  // Default: show as integer if whole number, otherwise 2 decimals
  return value % 1 === 0 ? value.toString() : value.toFixed(2);
}

